// Owns the per-room "current variant" selection and the before/after toggle.
// Pure state + events — no rendering side effects.

import type { Room, Variant } from '../app/types';
import { Emitter } from '../app/emitter';

export type VariantChangeTrigger =
  | 'init'
  | 'click'
  | 'beforeafter'
  | 'roomchange'
  | 'hotspot'
  | 'api';

export interface VariantChangeEvent {
  room: Room;
  previousVariant: Variant | null;
  currentVariant: Variant;
  trigger: VariantChangeTrigger;
}

export class VariantManager {
  private readonly emitter = new Emitter<VariantChangeEvent>();
  /** Per-room: which variant is currently selected. */
  private readonly currentByRoom = new Map<string, string>();
  /** Per-room: last non-before variant — restored when toggling back from "before". */
  private readonly lastAfterByRoom = new Map<string, string>();

  getCurrent(room: Room): Variant {
    const id = this.currentByRoom.get(room.id) ?? room.defaultVariant;
    const found = room.variants.find((v) => v.id === id);
    if (found) return found;
    // Defensive fallback — config drift shouldn't crash the user.
    const first = room.variants[0];
    if (!first) throw new Error(`Room ${room.id} has no variants`);
    return first;
  }

  setVariant(room: Room, variantId: string, trigger: VariantChangeTrigger = 'api'): boolean {
    const next = room.variants.find((v) => v.id === variantId);
    if (!next) return false;
    const prev = this.getCurrent(room);
    if (prev.id === next.id) return false;

    if (!prev.isBefore) {
      // Remember the "after" state we just left, so before/after can return to it.
      this.lastAfterByRoom.set(room.id, prev.id);
    }
    this.currentByRoom.set(room.id, next.id);
    this.emitter.emit({ room, previousVariant: prev, currentVariant: next, trigger });
    return true;
  }

  /**
   * Toggle between the room's "before" variant and the most recent "after" choice.
   * No-op if the room has no `beforeVariantId`.
   */
  toggleBeforeAfter(room: Room): { applied: boolean; isShowingBefore: boolean } {
    if (!room.beforeVariantId) return { applied: false, isShowingBefore: false };
    const current = this.getCurrent(room);
    if (current.isBefore) {
      const restoreId =
        this.lastAfterByRoom.get(room.id) ??
        (room.defaultVariant !== room.beforeVariantId
          ? room.defaultVariant
          : room.variants.find((v) => !v.isBefore)?.id);
      if (!restoreId) return { applied: false, isShowingBefore: true };
      this.setVariant(room, restoreId, 'beforeafter');
      return { applied: true, isShowingBefore: false };
    }
    this.setVariant(room, room.beforeVariantId, 'beforeafter');
    return { applied: true, isShowingBefore: true };
  }

  isShowingBefore(room: Room): boolean {
    return this.getCurrent(room).isBefore === true;
  }

  /**
   * Called when entering a room — fire an 'init' or 'roomchange' event so listeners can render
   * the panorama for the new room's current variant in a single code path.
   */
  emitForRoom(room: Room, trigger: VariantChangeTrigger = 'roomchange'): void {
    const current = this.getCurrent(room);
    this.emitter.emit({ room, previousVariant: null, currentVariant: current, trigger });
  }

  on(listener: (e: VariantChangeEvent) => void): () => void {
    return this.emitter.on(listener);
  }
}
